/**
 * Resolves the server-owned request contract for an Episode Library action.
 *
 * @param {Object} options
 * @param {'audio'|'youtube'|'apple'|'entire'} options.deleteType
 * @param {Object} options.episode
 * @param {string} options.showId
 * @param {string} options.episodeId
 * @returns {{url?: string, method?: string, body?: string, error?: string}}
 */
function destinationDeletionRequest({ deleteType, episode, showId, episodeId }) {
  if (deleteType === 'entire') {
    if (!episode?.delete_entire_form) {
      return { error: 'This episode cannot be deleted while video processing is active.' };
    }

    return {
      url: episode.delete_entire_form.action,
      method: episode.delete_entire_form.method.toUpperCase()
    };
  }

  if (deleteType === 'apple') {
    const form = episode?.remove_apple_form;
    const fallbackUrl = showId && episode?.episode_info_id
      ? `/shows/${encodeURIComponent(showId)}/episode_infos/${encodeURIComponent(episode.episode_info_id)}/destinations`
      : null;
    const url = form?.action || fallbackUrl;

    if (!url || !form?.method || !form?.fields) {
      return { error: 'Apple Video cannot be removed from this episode.' };
    }

    return {
      url,
      method: form.method.toUpperCase(),
      body: JSON.stringify(form.fields)
    };
  }

  if (deleteType === 'youtube') {
    return {
      url: `/shows/${showId}/episodes/${episodeId}/destroy-youtube-episode`,
      method: 'DELETE'
    };
  }

  return {
    url: `/shows/${showId}/episodes/${episodeId}/destroy-episode`,
    method: 'DELETE'
  };
}

/**
 * Builds the JSON request headers shared by Episode Library deletion actions.
 *
 * @param {string} csrfToken
 * @returns {{Accept: string, 'Content-Type': string, 'X-CSRF-Token': string}}
 */
function destinationDeletionRequestHeaders(csrfToken) {
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-CSRF-Token': csrfToken
  };
}

/**
 * Reads JSON deletion responses without trying to parse an HTML error page.
 *
 * @param {Response} response
 * @returns {Promise<Object>}
 */
function destinationDeletionResponsePayload(response) {
  const contentType = response.headers?.get('content-type') || '';
  return contentType.includes('application/json') ? response.json() : Promise.resolve({});
}

/**
 * Returns whether a deletion response should refresh the Episode Library.
 *
 * @param {Object} options
 * @param {'audio'|'youtube'|'apple'|'entire'} options.deleteType
 * @param {boolean} options.responseOk
 * @param {Object} options.payload
 * @returns {boolean}
 */
function destinationDeletionSucceeded({ deleteType, responseOk, payload }) {
  return Boolean(
    payload.episode_deleted ||
    payload.deletion_started ||
    (deleteType === 'apple' && responseOk)
  );
}

if (typeof window !== 'undefined') {
  window.episodeLibraryDestinationDeletion = {
    request: destinationDeletionRequest,
    requestHeaders: destinationDeletionRequestHeaders,
    responsePayload: destinationDeletionResponsePayload,
    succeeded: destinationDeletionSucceeded
  };
}

module.exports = {
  destinationDeletionRequest,
  destinationDeletionRequestHeaders,
  destinationDeletionResponsePayload,
  destinationDeletionSucceeded
};
